if (!customElements.get('product-info')) {
  function escapeHtmlForEaseSaleBadge(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            const easeSticky =
              document.getElementById('shopify-section-ease_sticky_atc') ||
              document.getElementById('shopify-section-ease-sticky-atc-bar');
            const defaultSticky = document.getElementById('shopify-section-sticky-atc-bar');
            const stickyEl = easeSticky || defaultSticky;
            const stickyNext = easeSticky
              ? html.getElementById('shopify-section-ease_sticky_atc') ||
                html.getElementById('shopify-section-ease-sticky-atc-bar')
              : html.getElementById('shopify-section-sticky-atc-bar');
            if (stickyEl && stickyNext) {
              HTMLUpdateUtility.viewTransition(
                stickyEl,
                stickyNext,
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);
          const isSalespageV1 =
            document.body.classList.contains('product-template') &&
            document.body.classList.contains('salespage-v1');

          this.pickupAvailability?.update(variant);
          if (!isSalespageV1) {
            this.updateOptionValues(html);
          }
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (isSalespageV1) {
            this.syncSelectedVariantJsonFromFetchedSection(html);
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          if (isSalespageV1) {
            this.updateSalespagePrice(variant);
            this.updateSalespageBundlePricing(variant);
          } else {
            updateSourceFromDestination('price');
          }
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      updateSalespagePrice(variant) {
        if (!variant) return;

        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceContainer) return;

        const priceRoot = priceContainer.querySelector('.ease-salespage-info-price');
        const innerContainer = priceContainer.querySelector('.price__container');
        if (!priceRoot || !innerContainer) return;

        const moneyFormat = FoxTheme.settings.moneyFormat;
        const currencyEnabled = !!FoxTheme.settings.currencyCodeEnabled;
        const formatMoney = (amount) =>
          currencyEnabled
            ? FoxTheme.Currency.formatMoney(amount, FoxTheme.settings.moneyWithCurrencyFormat || moneyFormat)
            : FoxTheme.Currency.formatMoney(amount, moneyFormat);

        const compareAt = variant.compare_at_price;
        const price = variant.price;
        const onSale = !!(compareAt && compareAt > price);
        const available = !!variant.available;

        priceRoot.classList.toggle('price--on-sale', onSale);
        priceRoot.classList.toggle('price--sold-out', !available);

        const badgeLine = priceContainer.dataset.easeSaleBadgeLine || '-10 %';
        const badgeTextColor = priceContainer.dataset.easeSaleBadgeText || '#c73a3a';
        const badgeBgColor = priceContainer.dataset.easeSaleBadgeBg || badgeTextColor;
        priceContainer.style.setProperty('--ease-sp-badge-text', badgeTextColor);
        priceContainer.style.setProperty('--ease-sp-badge-bg', badgeBgColor);

        if (onSale) {
          innerContainer.innerHTML = `
            <div class="price__sale">
              <span class="price-item price-item--sale price-item--last">${formatMoney(price)}</span>
              <span><s class="price-item price-item--regular">${formatMoney(compareAt)}</s></span>
              <span class="ease-salespage-info-price__saving">${escapeHtmlForEaseSaleBadge(badgeLine)}</span>
            </div>
          `;
        } else {
          innerContainer.innerHTML = `
            <div class="price__regular">
              <span class="price-item price-item--regular">${formatMoney(price)}</span>
            </div>
          `;
        }
      }

      /**
       * Buy-buttons bundle block (Mit dabei / Gesamt): auf salespage-v1 wird variant-selects im DOM
       * nicht per fetch aktualisiert - Bundle-Preis daher hier mit derselben Varianten-Quelle wie der Kopfpreis.
       */
      updateSalespageBundlePricing(variant) {
        if (!variant || variant.price == null) return;

        const bundleEl = this.querySelector('#bundle-price-summary-' + this.dataset.section);
        if (!bundleEl) return;

        const moneyFormat = FoxTheme.settings.moneyFormat;
        const currencyEnabled = !!FoxTheme.settings.currencyCodeEnabled;
        const formatMoney = (amount) =>
          currencyEnabled
            ? FoxTheme.Currency.formatMoney(amount, FoxTheme.settings.moneyWithCurrencyFormat || moneyFormat)
            : FoxTheme.Currency.formatMoney(amount, moneyFormat);

        const extrasEuros = parseFloat(bundleEl.dataset.totalSavings || '0', 10) || 0;
        const extrasCents = Math.round(extrasEuros * 100);
        const originalBaseCents = variant.compare_at_price || variant.price;
        const fullOldCents = originalBaseCents + extrasCents;
        const finalCents = variant.price;
        const savingsCents = Math.max(0, fullOldCents - finalCents);

        const originalTotalEl = bundleEl.querySelector('.original-total');
        const finalTotalEl = bundleEl.querySelector('.final-total');
        const ersparnisMoneyEl = bundleEl.querySelector('.ease-sp-bundle__ersparnis-money');
        const ersparnisPctEl = bundleEl.querySelector('.ease-sp-bundle__ersparnis-pct');
        const ersparnisLegacyEl =
          !ersparnisMoneyEl && bundleEl.querySelector('.ease-sp-bundle__ersparnis');

        if (originalTotalEl) originalTotalEl.textContent = formatMoney(fullOldCents);
        if (finalTotalEl) finalTotalEl.textContent = formatMoney(finalCents);

        const pct =
          fullOldCents > 0 && savingsCents > 0
            ? Math.round((savingsCents / fullOldCents) * 100)
            : 0;
        const htmlLang = (document.documentElement.getAttribute('lang') || 'de').split('-')[0].toLowerCase();
        const pctLabel = htmlLang === 'en' ? '(' + pct + '%)' : '(−' + pct + ' %)';

        if (ersparnisMoneyEl) {
          ersparnisMoneyEl.textContent = '-' + formatMoney(savingsCents);
          if (ersparnisPctEl) {
            if (pct > 0) {
              ersparnisPctEl.textContent = pctLabel;
              ersparnisPctEl.removeAttribute('hidden');
            } else {
              ersparnisPctEl.textContent = '';
              ersparnisPctEl.setAttribute('hidden', '');
            }
          }
        } else if (ersparnisLegacyEl) {
          ersparnisLegacyEl.textContent = '-' + formatMoney(savingsCents);
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const el = productInfoNode.querySelector('variant-selects script[data-selected-variant]');
        const raw =
          el?.textContent?.trim() ||
          productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        if (!raw) return null;
        try {
          return JSON.parse(raw);
        } catch (e) {
          return null;
        }
      }

      /**
       * Salespage-v1: updateOptionValues() wird absichtlich nicht ausgeführt (kein kompletter Austausch von variant-selects).
       * Ohne Sync bleibt <script data-selected-variant> auf der ersten Variante; media-gallery init() liest nach 100ms
       * dieses veraltete JSON und sortiert die Galerie zurück (z. B. auf Gold).
       */
      syncSelectedVariantJsonFromFetchedSection(html) {
        const sectionId = this.dataset.section;
        const sourceVs =
          html?.querySelector(`variant-selects[data-section="${sectionId}"]`) ||
          html?.querySelector('variant-selects');
        const destVs =
          this.querySelector(`variant-selects[data-section="${sectionId}"]`) || this.querySelector('variant-selects');
        if (!sourceVs || !destVs) return;
        const src = sourceVs.querySelector('script[data-selected-variant]');
        const dest = destVs.querySelector('script[data-selected-variant]');
        if (!src || !dest) return;
        const text = (src.textContent && src.textContent.trim()) || src.innerHTML;
        if (!text) return;
        dest.textContent = text;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(`product-form-${this.dataset.section}`);
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (!addButton) return;
        addButtonText.textContent = FoxTheme.variantStrings.unavailable;
        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}
